/* eslint-disable react/no-unknown-property */
/* ============================================================
   Characters – articulated humans, dogs and aliens.
   Geometry is shared at module level, materials per instance
   (few) and all animation runs on refs (no React re-renders).
   ============================================================ */
import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

const SKIN = ["#F1C9A5", "#D9A57C", "#B07B52", "#8D5A3A", "#F5D6BE", "#C68E6A"];
const HAIR = ["#1B1410", "#3A2A1C", "#6B4A2B", "#B58A4E", "#2A2A2E", "#8A3B23", "#D8C5A0"];
const PANTS = ["#2A3A5A", "#1F2430", "#4A4E57", "#6B5A47", "#243B2A", "#8A8378"];
const SHOES = ["#1A1612", "#3A2A20", "#F2F2F2", "#2A2E3A"];
const DOG_COATS = [
  { body: "#8A6540", accent: "#5A3E28" },
  { body: "#2A2622", accent: "#EDE6DA" },
  { body: "#D9B37A", accent: "#B58A4E" },
  { body: "#8B8681", accent: "#5A5652" },
];

/* ---------- shared geometry ---------- */
const HUMAN_G = (() => {
  const torso = new THREE.CylinderGeometry(0.115, 0.095, 0.32, 12);
  torso.scale(1, 1, 0.62);
  const capBrim = new THREE.CylinderGeometry(0.1, 0.1, 0.02, 14);
  capBrim.scale(1, 1, 1.35);
  capBrim.translate(0, 0, 0.03);
  return {
    thigh: new THREE.CapsuleGeometry(0.045, 0.16, 3, 8),
    shin: new THREE.CapsuleGeometry(0.035, 0.16, 3, 8),
    shoe: new THREE.BoxGeometry(0.085, 0.05, 0.15),
    torso,
    neck: new THREE.CylinderGeometry(0.035, 0.04, 0.06, 8),
    head: new THREE.SphereGeometry(0.085, 14, 12),
    hair: new THREE.SphereGeometry(0.09, 12, 8, 0, Math.PI * 2, 0, 1.85),
    longHair: new THREE.SphereGeometry(0.08, 10, 8),
    capDome: new THREE.SphereGeometry(0.092, 12, 8, 0, Math.PI * 2, 0, 1.4),
    capBrim,
    eye: new THREE.SphereGeometry(0.012, 6, 6),
    upperArm: new THREE.CapsuleGeometry(0.035, 0.12, 3, 8),
    forearm: new THREE.CapsuleGeometry(0.03, 0.11, 3, 8),
    hand: new THREE.SphereGeometry(0.035, 8, 6),
  };
})();

const ALIEN_G = (() => {
  const torso = new THREE.CylinderGeometry(0.075, 0.06, 0.3, 12);
  torso.scale(1, 1, 0.7);
  const cranium = new THREE.SphereGeometry(0.14, 18, 14);
  cranium.scale(1, 1.12, 1);
  const jaw = new THREE.SphereGeometry(0.078, 14, 10);
  jaw.scale(0.92, 1.05, 0.95);
  const eye = new THREE.SphereGeometry(0.055, 14, 10);
  eye.scale(1.55, 0.95, 0.5);
  return {
    thigh: new THREE.CapsuleGeometry(0.03, 0.16, 3, 8),
    shin: new THREE.CapsuleGeometry(0.025, 0.15, 3, 8),
    foot: new THREE.SphereGeometry(0.04, 8, 6),
    torso,
    neck: new THREE.CylinderGeometry(0.025, 0.03, 0.08, 8),
    cranium,
    jaw,
    eye,
    glint: new THREE.SphereGeometry(0.012, 6, 6),
    mouth: new THREE.BoxGeometry(0.05, 0.006, 0.01),
    upperArm: new THREE.CapsuleGeometry(0.022, 0.2, 3, 8),
    forearm: new THREE.CapsuleGeometry(0.02, 0.2, 3, 8),
    hand: new THREE.SphereGeometry(0.028, 8, 6),
    badge: new THREE.SphereGeometry(0.02, 8, 6),
  };
})();

const DOG_G = (() => {
  const body = new THREE.CapsuleGeometry(0.06, 0.18, 4, 10);
  body.rotateX(Math.PI / 2);
  const ear = new THREE.SphereGeometry(0.03, 8, 6);
  ear.scale(0.6, 1, 0.4);
  return {
    body,
    head: new THREE.SphereGeometry(0.055, 12, 10),
    snout: new THREE.BoxGeometry(0.05, 0.04, 0.06),
    nose: new THREE.SphereGeometry(0.012, 6, 6),
    ear,
    eye: new THREE.SphereGeometry(0.009, 6, 6),
    leg: new THREE.CapsuleGeometry(0.018, 0.08, 2, 6),
    tail: new THREE.CapsuleGeometry(0.012, 0.1, 2, 6),
    collar: new THREE.TorusGeometry(0.05, 0.008, 6, 14),
  };
})();

/* materials per instance, disposed on unmount */
function useMats(spec) {
  const mats = useMemo(() => {
    const out = {};
    Object.entries(spec).forEach(([k, v]) => {
      out[k] = new THREE.MeshStandardMaterial(typeof v === "string" ? { color: v, roughness: 0.85 } : v);
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, Object.values(spec).map((v) => (typeof v === "string" ? v : JSON.stringify(v))));
  useEffect(() => () => Object.values(mats).forEach((m) => m.dispose()), [mats]);
  return mats;
}

/* ============================================================
   Human – walks in a circle around (x, z), swinging limbs
   Model forward = +Z, unit height 1.0, feet at y = 0
   ============================================================ */
export function TinyPerson({ x, z, color = "#5A99CC", seed = 0 }) {
  const ref = useRef();
  const body = useRef();
  const head = useRef();
  const lArm = useRef();
  const rArm = useRef();
  const lElbow = useRef();
  const rElbow = useRef();
  const lLeg = useRef();
  const rLeg = useRef();
  const lKnee = useRef();
  const rKnee = useRef();
  const tmp = useMemo(() => new THREE.Vector3(), []);

  const look = useMemo(
    () => ({
      skin: SKIN[seed % SKIN.length],
      hair: HAIR[(seed * 3 + 1) % HAIR.length],
      pants: PANTS[(seed * 5 + 2) % PANTS.length],
      shoes: SHOES[(seed * 7 + 1) % SHOES.length],
      style: seed % 4, // 0 short, 1 long, 2 cap, 3 buzz
      gait: 5.2 + (seed % 3) * 0.7,
      radius: 2.0 + (seed % 3) * 0.4,
      speed: 0.38 + (seed % 2) * 0.1,
      height: 0.62 * (0.95 + ((seed * 13) % 5) * 0.025),
    }),
    [seed]
  );
  const mats = useMats({
    skin: { color: look.skin, roughness: 0.75 },
    hair: { color: look.hair, roughness: 0.9 },
    shirt: { color, roughness: 0.9 },
    pants: { color: look.pants, roughness: 0.95 },
    shoes: { color: look.shoes, roughness: 0.6 },
    eye: { color: "#0B0A0C", roughness: 0.3 },
  });

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const a = t * look.speed + seed * 1.3;
    ref.current.position.x = x + Math.cos(a) * look.radius;
    ref.current.position.z = z + Math.sin(a) * look.radius;
    ref.current.rotation.y = -a;
    const ph = t * look.gait + seed;
    const sw = Math.sin(ph);
    if (lLeg.current) lLeg.current.rotation.x = sw * 0.6;
    if (rLeg.current) rLeg.current.rotation.x = -sw * 0.6;
    if (lKnee.current) lKnee.current.rotation.x = Math.max(0, Math.sin(ph - 0.7)) * 0.9;
    if (rKnee.current) rKnee.current.rotation.x = Math.max(0, Math.sin(ph + Math.PI - 0.7)) * 0.9;
    if (lArm.current) lArm.current.rotation.x = -sw * 0.5;
    if (rArm.current) rArm.current.rotation.x = sw * 0.5;
    if (lElbow.current) lElbow.current.rotation.x = -0.35 - Math.max(0, -sw) * 0.3;
    if (rElbow.current) rElbow.current.rotation.x = -0.35 - Math.max(0, sw) * 0.3;
    if (body.current) body.current.position.y = 0.5 + Math.abs(Math.sin(ph)) * 0.02;
    if (head.current) {
      head.current.rotation.y = Math.sin(t * 0.7 + seed) * 0.35;
      head.current.rotation.x = Math.sin(t * 0.9 + seed * 2) * 0.06;
    }
    ref.current.getWorldPosition(tmp);
    const d = state.camera.position.distanceTo(tmp);
    // Humans stay readable from the air: 3x baseline, up to 8x when close
    const scale = (3 + Math.max(0, (140 - d) / 140) * 5) * look.height;
    ref.current.scale.setScalar(scale);
  });

  const G = HUMAN_G;
  const renderLeg = (side, legRef, kneeRef) => (
    <group ref={legRef} position={[side * 0.07, 0.5, 0]}>
      <mesh geometry={G.thigh} material={mats.pants} position={[0, -0.12, 0]} />
      <group ref={kneeRef} position={[0, -0.24, 0]}>
        <mesh geometry={G.shin} material={mats.pants} position={[0, -0.1, 0]} />
        <mesh geometry={G.shoe} material={mats.shoes} position={[0, -0.215, 0.025]} />
      </group>
    </group>
  );
  const renderArm = (side, armRef, elbowRef) => (
    <group ref={armRef} position={[side * 0.15, 0.28, 0]}>
      <mesh geometry={G.upperArm} material={mats.shirt} position={[0, -0.09, 0]} />
      <group ref={elbowRef} position={[0, -0.18, 0]}>
        <mesh geometry={G.forearm} material={mats.skin} position={[0, -0.085, 0]} />
        <mesh geometry={G.hand} material={mats.skin} position={[0, -0.18, 0]} />
      </group>
    </group>
  );

  return (
    <group ref={ref} position={[x, 0.3, z]}>
      {renderLeg(-1, lLeg, lKnee)}
      {renderLeg(1, rLeg, rKnee)}
      <group ref={body} position={[0, 0.5, 0]}>
        {/* torso (origin at hips) */}
        <mesh geometry={G.torso} material={mats.shirt} position={[0, 0.16, 0]} />
        {/* belt */}
        <mesh position={[0, 0.01, 0]}>
          <cylinderGeometry args={[0.1, 0.1, 0.03, 12]} />
          <meshStandardMaterial color="#2A1F1C" roughness={0.7} />
        </mesh>
        <mesh geometry={G.neck} material={mats.skin} position={[0, 0.34, 0]} />
        <group ref={head} position={[0, 0.415, 0]}>
          <mesh geometry={G.head} material={mats.skin} />
          {look.style === 0 && <mesh geometry={G.hair} material={mats.hair} position={[0, 0.012, -0.012]} />}
          {look.style === 1 && (
            <>
              <mesh geometry={G.hair} material={mats.hair} position={[0, 0.012, -0.012]} />
              <mesh geometry={G.longHair} material={mats.hair} position={[0, -0.05, -0.06]} scale={[1, 1.3, 0.7]} />
            </>
          )}
          {look.style === 2 && (
            <>
              <mesh geometry={G.capDome} position={[0, 0.02, 0]}>
                <meshStandardMaterial color="#B01A2E" roughness={0.8} />
              </mesh>
              <mesh geometry={G.capBrim} position={[0, 0.02, 0.05]}>
                <meshStandardMaterial color="#B01A2E" roughness={0.8} />
              </mesh>
            </>
          )}
          {look.style === 3 && <mesh geometry={G.hair} material={mats.hair} position={[0, 0.01, -0.01]} scale={[0.98, 0.9, 0.98]} />}
          <mesh geometry={G.eye} material={mats.eye} position={[0.03, 0.01, 0.078]} />
          <mesh geometry={G.eye} material={mats.eye} position={[-0.03, 0.01, 0.078]} />
        </group>
        {renderArm(-1, lArm, lElbow)}
        {renderArm(1, rArm, rElbow)}
      </group>
    </group>
  );
}

/* ============================================================
   Dog – trots around, wags tail. Forward = +Z
   ============================================================ */
export function TinyDog({ x, z, seed = 0 }) {
  const ref = useRef();
  const legs = useRef([]);
  const tail = useRef();
  const head = useRef();
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const coat = DOG_COATS[seed % DOG_COATS.length];
  const mats = useMats({
    body: { color: coat.body, roughness: 0.95 },
    accent: { color: coat.accent, roughness: 0.95 },
    dark: { color: "#111013", roughness: 0.4 },
    collar: { color: "#E94B4B", roughness: 0.6 },
  });
  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    const r = 3;
    const a = t * 0.6 + seed * 0.7;
    ref.current.position.x = x + Math.cos(a) * r;
    ref.current.position.z = z + Math.sin(a) * r;
    ref.current.rotation.y = -a;
    const ph = t * 9 + seed;
    legs.current.forEach((l, i) => {
      if (!l) return;
      const phase = i === 0 || i === 3 ? 0 : Math.PI;
      l.rotation.x = Math.sin(ph + phase) * 0.55;
    });
    if (tail.current) tail.current.rotation.y = Math.sin(t * 12 + seed) * 0.6;
    if (head.current) head.current.rotation.y = Math.sin(t * 1.3 + seed) * 0.4;
    ref.current.getWorldPosition(tmp);
    const d = state.camera.position.distanceTo(tmp);
    const scale = Math.max(1, 1 + Math.max(0, (80 - d) / 80) * 3.5) * 1.15;
    ref.current.scale.setScalar(scale);
  });
  const G = DOG_G;
  return (
    <group ref={ref} position={[x, 0.2, z]}>
      <mesh geometry={G.body} material={mats.body} position={[0, 0.16, 0]} />
      <group ref={head} position={[0, 0.21, 0.14]}>
        <mesh geometry={G.head} material={mats.body} />
        <mesh geometry={G.snout} material={mats.accent} position={[0, -0.015, 0.055]} />
        <mesh geometry={G.nose} material={mats.dark} position={[0, -0.005, 0.087]} />
        <mesh geometry={G.ear} material={mats.accent} position={[0.045, 0.03, -0.01]} rotation={[0, 0, -0.4]} />
        <mesh geometry={G.ear} material={mats.accent} position={[-0.045, 0.03, -0.01]} rotation={[0, 0, 0.4]} />
        <mesh geometry={G.eye} material={mats.dark} position={[0.022, 0.015, 0.045]} />
        <mesh geometry={G.eye} material={mats.dark} position={[-0.022, 0.015, 0.045]} />
      </group>
      <mesh geometry={G.collar} material={mats.collar} position={[0, 0.19, 0.1]} rotation={[Math.PI / 2, 0, 0]} />
      {[
        [0.04, 0.07],
        [-0.04, 0.07],
        [0.04, -0.07],
        [-0.04, -0.07],
      ].map(([lx, lz], i) => (
        <group key={i} ref={(el) => (legs.current[i] = el)} position={[lx, 0.12, lz]}>
          <mesh geometry={G.leg} material={mats.body} position={[0, -0.05, 0]} />
        </group>
      ))}
      <group ref={tail} position={[0, 0.2, -0.11]}>
        <mesh geometry={G.tail} material={mats.body} position={[0, 0.04, -0.04]} rotation={[-0.9, 0, 0]} />
      </group>
    </group>
  );
}

/* ============================================================
   Alien – classic "grey" proportions with a green-grey skin:
   large cranium, tapered jaw, huge glossy almond eyes, slender
   body, long arms with three fingers. Idle animation + blink.
   Outer group receives the radial orientation from the planet.
   ============================================================ */
export function TinyAlien({ x = 0, y = 0, z = 0, rx = 0, ry = 0, rz = 0, seed = 0, staticPose = false, baseScale }) {
  const ref = useRef();
  const inner = useRef();
  const head = useRef();
  const lEye = useRef();
  const rEye = useRef();
  const lArm = useRef();
  const rArm = useRef();
  const torso = useRef();
  const badge = useRef();
  const tmp = useMemo(() => new THREE.Vector3(), []);
  const waves = seed % 3 === 0;
  const mats = useMats({
    skin: { color: "#8FBF98", roughness: 0.55, metalness: 0.05, emissive: "#2F6B3C", emissiveIntensity: 0.28 },
    skinDark: { color: "#6FA37A", roughness: 0.6, emissive: "#234F2C", emissiveIntensity: 0.2 },
    eye: { color: "#05080A", roughness: 0.05, metalness: 0.35 },
    suit: { color: "#3B4652", roughness: 0.5, metalness: 0.4 },
    badge: { color: "#F0A020", emissive: "#F0A020", emissiveIntensity: 2.2, toneMapped: false },
  });
  const glintMat = useMemo(() => new THREE.MeshBasicMaterial({ color: "#EAFBFF", toneMapped: false }), []);
  useEffect(() => () => glintMat.dispose(), [glintMat]);

  useFrame((state) => {
    if (!ref.current) return;
    const t = state.clock.elapsedTime;
    if (!staticPose && inner.current) {
      inner.current.position.y = Math.abs(Math.sin(t * 2.2 + seed)) * 0.03;
      inner.current.rotation.y = Math.sin(t * 0.4 + seed) * 0.5 + seed * 0.7;
    }
    if (head.current) {
      head.current.rotation.z = Math.sin(t * 0.9 + seed) * 0.12;
      head.current.rotation.y = Math.sin(t * 0.6 + seed * 2) * 0.4;
      head.current.rotation.x = Math.sin(t * 0.8 + seed) * 0.08;
    }
    // blink every ~4s
    const blink = (t * 0.6 + seed * 0.37) % 3.2 < 0.09 ? 0.1 : 1;
    if (lEye.current) lEye.current.scale.y = blink;
    if (rEye.current) rEye.current.scale.y = blink;
    if (torso.current) torso.current.scale.y = 1 + Math.sin(t * 1.6 + seed) * 0.015;
    if (lArm.current) lArm.current.rotation.x = Math.sin(t * 1.1 + seed) * 0.12;
    if (rArm.current) {
      if (waves) {
        rArm.current.rotation.x = -2.4 + Math.sin(t * 3.5 + seed) * 0.25;
        rArm.current.rotation.z = 0.55 + Math.sin(t * 3.5 + seed) * 0.15;
      } else {
        rArm.current.rotation.x = -Math.sin(t * 1.1 + seed) * 0.12;
      }
    }
    if (badge.current) badge.current.material.emissiveIntensity = 1.6 + Math.sin(t * 5 + seed) * 0.8;
    ref.current.getWorldPosition(tmp);
    const d = state.camera.position.distanceTo(tmp);
    // Aliens read from far away: 6x baseline, up to 12x when close
    const s = baseScale != null ? baseScale : 6 + Math.max(0, (200 - d) / 200) * 6;
    ref.current.scale.setScalar(s);
  });

  const G = ALIEN_G;
  const renderLeg = (side) => (
    <group position={[side * 0.055, 0.42, 0]}>
      <mesh geometry={G.thigh} material={mats.suit} position={[0, -0.11, 0]} />
      <mesh geometry={G.shin} material={mats.skinDark} position={[0, -0.3, 0]} />
      <mesh geometry={G.foot} material={mats.skinDark} position={[0, -0.4, 0.02]} scale={[1, 0.5, 1.4]} />
    </group>
  );
  const renderArm = (side, armRef) => (
    <group ref={armRef} position={[side * 0.095, 0.7, 0]}>
      <mesh geometry={G.upperArm} material={mats.skin} position={[0, -0.12, 0]} />
      <group position={[0, -0.25, 0]} rotation={[-0.25, 0, 0]}>
        <mesh geometry={G.forearm} material={mats.skin} position={[0, -0.12, 0]} />
        {/* elongated hand (fingers merged into a single flattened shape to save draw calls) */}
        <mesh geometry={G.hand} material={mats.skin} position={[0, -0.27, 0]} scale={[1, 1.6, 0.7]} />
      </group>
    </group>
  );

  return (
    <group ref={ref} position={[x, y, z]} rotation={[rx, ry, rz]}>
      <group ref={inner}>
        {renderLeg(-1)}
        {renderLeg(1)}
        <group ref={torso} position={[0, 0.42, 0]}>
          <mesh geometry={G.torso} material={mats.suit} position={[0, 0.15, 0]} />
          {/* glowing comms badge */}
          <mesh ref={badge} geometry={G.badge} material={mats.badge} position={[0.03, 0.22, 0.05]} />
          <mesh geometry={G.neck} material={mats.skin} position={[0, 0.33, 0]} />
          <group ref={head} position={[0, 0.38, 0]}>
            <mesh geometry={G.jaw} material={mats.skin} position={[0, 0.03, 0.01]} />
            <mesh geometry={G.cranium} material={mats.skin} position={[0, 0.15, -0.01]} />
            {/* eyes: big glossy almonds, tilted outward */}
            <mesh ref={lEye} geometry={G.eye} material={mats.eye} position={[-0.075, 0.13, 0.115]} rotation={[0.1, -0.35, 0.45]} />
            <mesh ref={rEye} geometry={G.eye} material={mats.eye} position={[0.075, 0.13, 0.115]} rotation={[0.1, 0.35, -0.45]} />
            <mesh geometry={G.glint} material={glintMat} position={[-0.062, 0.15, 0.152]} />
            <mesh geometry={G.glint} material={glintMat} position={[0.088, 0.15, 0.15]} />
            {/* mouth */}
            <mesh geometry={G.mouth} material={mats.eye} position={[0, 0.02, 0.075]} />
          </group>
        </group>
        {renderArm(-1, lArm)}
        {renderArm(1, rArm)}
        <pointLight color="#7FD98F" intensity={0.45} distance={2.4} position={[0, 0.6, 0.3]} />
      </group>
    </group>
  );
}
